import { useTranslation } from 'react-i18next';
import { WarningIcon } from '@phosphor-icons/react';
import type { NodeDeletionPreviewDto } from '@monorepo/shared';
import { statusBadgeConfig } from './table/documentTableTypes';

const MAX_LISTED_DOCUMENTS = 10;

interface DeleteNodePreviewProps {
  preview: NodeDeletionPreviewDto | undefined;
  isLoading: boolean;
}

export const DeleteNodePreview = ({ preview, isLoading }: DeleteNodePreviewProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <p className="text-sm text-gray-500">{t('documents.delete_document_modal.loading')}</p>;
  }

  if (!preview) {
    return null;
  }

  const { isFolder, folderCount, documentCount, sensitiveDocuments } = preview;
  const listedDocuments = sensitiveDocuments.slice(0, MAX_LISTED_DOCUMENTS);
  const remainingCount = sensitiveDocuments.length - listedDocuments.length;
  const isEmptyFolder = folderCount === 0 && documentCount === 0;

  return (
    <div className="flex flex-col gap-3">
      {isFolder && (
        <p className="text-sm text-gray-600">
          {isEmptyFolder
            ? t('documents.delete_document_modal.empty_folder')
            : t('documents.delete_document_modal.contents_summary', {
                folders: folderCount,
                documents: documentCount,
              })}
        </p>
      )}

      {sensitiveDocuments.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-orange-800">
            <WarningIcon size={18} weight="fill" />
            {t('documents.delete_document_modal.sensitive_title', {
              count: sensitiveDocuments.length,
            })}
          </div>

          <ul className="flex flex-col gap-1">
            {listedDocuments.map((sensitiveDocument) => {
              const { className, label } = statusBadgeConfig[sensitiveDocument.status];

              return (
                <li
                  key={sensitiveDocument.id}
                  className="flex flex-wrap items-center gap-2 text-sm text-orange-900"
                >
                  <span className="font-medium">{sensitiveDocument.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${className}`}>
                    {t(label)}
                  </span>
                </li>
              );
            })}
          </ul>

          {remainingCount > 0 && (
            <p className="text-xs text-orange-800">
              {t('documents.delete_document_modal.sensitive_more', { count: remainingCount })}
            </p>
          )}

          <p className="text-xs text-orange-800">
            {t('documents.delete_document_modal.sensitive_hint')}
          </p>
        </div>
      )}
    </div>
  );
};
